import { css } from '@emotion/react';
import {
  type FC, useMemo,
} from 'react';
import { TextInputComponent } from '~/_shared/baseComponents/inputs';
import { DebouncedUpdateComponent } from '~/_shared/components/delay/debouncedUpdate.component';
import type { SliderValue } from '~/_shared/components/slider/slider.component';
import { SliderWithValueLabelsComponent } from '~/_shared/components/slider/sliderWithValueLabels/sliderWithValueLabels.component';
import { useHandleSliderOption } from '~/_shared/utils/hooks/useHandleSliderOption.hook';
import { roundNumberDecimalPlaces } from '~/_shared/utils/number/number.helpers';

const SLIDER_INPUT_WIDTH = 60;

const headingStyle = css({
  margin: '0 0 10px',
  textTransform: 'uppercase',
  fontSize: '16px',
  lineHeight: 1.2,
});

const sliderWrapperStyle = css({
  alignItems: 'center',
  display: 'flex',
  gap: 12,
});

const sliderStyle = css({
  flex: 1,
  paddingBottom: 0,
});

const sliderInputStyle = css({
  width: SLIDER_INPUT_WIDTH,
});

const unitStyle = css({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
});

type SliderWithUnitInputProps = Readonly<{
  className?: string;
  disabled?: boolean;
  label?: string;
  max: number;
  min: number;
  unit: string;
  value: number;
  onChange: (value: number) => void;
}>;

export const SliderWithUnitInputComponent: FC<SliderWithUnitInputProps> = ({
  className,
  disabled,
  label,
  max,
  min,
  unit,
  value,
  onChange,
}) => {
  const sliderHandlers = useHandleSliderOption(value, onChange, min, max, unit);

  const roundedValue = useMemo(() => {
    const numValue = Number(sliderHandlers.inputValue);

    return isNaN(numValue) ? sliderHandlers.inputValue : roundNumberDecimalPlaces(numValue, 0).toString();
  }, [sliderHandlers.inputValue]);

  const unitContent = useMemo(() => (
    <div css={unitStyle}>
      {unit}
    </div>
  ), [unit]);

  return (
    <div className={className}>
      {label && <p css={headingStyle}>{label}</p>}
      <div css={sliderWrapperStyle}>
        <DebouncedUpdateComponent<SliderValue>
          onChangeDebounced={sliderHandlers.onSliderChange}
          render={(sliderValue, onSliderChange) => (
            <SliderWithValueLabelsComponent
              css={sliderStyle}
              currentValueOnTheRight
              disabled={disabled}
              max={max}
              min={min}
              noLabel
              onChange={onSliderChange}
              step={1}
              value={sliderValue}
            />
          )}
          value={[value]}
          wait={50}
        />
        <TextInputComponent
          css={sliderInputStyle}
          icon={null}
          isDisabled={disabled}
          onBlur={() => sliderHandlers.onInputBlur(roundedValue)}
          onChange={sliderHandlers.onTextInputChange}
          rightContent={unitContent}
          value={roundedValue}
        />
      </div>
    </div>
  );
};
